// SMTP / Email tab (admin).
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useEffect } from 'react';
import { postJson, putJson, fetchJson } from '../../api.js';

// SmtpPanel — admin-only. Edit the email-sending credentials used for
// statements. Admin picks the transport mode (Microsoft Graph vs SMTP);
// finance/accounting users never see this tab.
export default function SmtpPanel() {
  // Data: email settings (Microsoft Graph or SMTP) and the form being edited.
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [msg, setMsg]         = useState(null);
  const [busy, setBusy]       = useState(false);
  const [testTo, setTestTo]   = useState('');
  const [form, setForm]       = useState({
    mode: 'graph',
    // graph
    tenantId: '', clientId: '', clientSecret: '',
    // smtp
    host: 'smtp.office365.com', port: '587', user: '', pass: '',
    // both
    from: '', fromName: 'EcoVac Solutions Ltd.', summaryRecipient: ''
  });

  // Actions: load settings, save, send a test email.
  async function load() {
    setLoading(true); setErr(null);
    try {
      const r = await fetchJson('/api/settings/smtp-config');
      setCfg(r);
      setForm({
        mode:         r.config.mode || 'graph',
        tenantId:     r.config.tenantId || '',
        clientId:     r.config.clientId || '',
        clientSecret: r.config.clientSecretMasked || '',
        host:         r.config.host || 'smtp.office365.com',
        port:         r.config.port || '587',
        user:         r.config.user || '',
        pass:         r.config.passwordMasked || '',
        from:         r.config.from || '',
        fromName:     r.config.fromName || 'EcoVac Solutions Ltd.',
        summaryRecipient: r.config.summaryRecipient || ''
      });
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setBusy(true); setMsg(null); setErr(null);
    try {
      await putJson('/api/settings/smtp-config', form);
      setMsg('Saved. Send a test email to verify credentials work.');
      load();
    } catch (e) { setErr('Save failed: ' + e.message); }
    finally { setBusy(false); }
  }

  async function sendTest() {
    if (!testTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo)) {
      setMsg('Enter a valid test recipient email first.');
      return;
    }
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await postJson('/api/settings/smtp-test', { to: testTo });
      if (r && r.ok) setMsg('Test email sent to ' + testTo + '. Check the inbox.');
      else setErr('Test failed: ' + ((r && r.reason) || 'unknown'));
    } catch (e) { setErr('Test failed: ' + e.message); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="card" style={{ padding: '0.75rem' }}>Loading email config…</div>;

  const isGraph = form.mode === 'graph';

  // Screen: mode picker, settings for the chosen mode, internal summary recipient, Save + test.
  return (
    <div className="card" style={{ padding: '1rem', maxWidth: '52em' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Email configuration</h3>
        {cfg && (
          <span className="small" style={{ color: '#666' }}>
            source: <strong>{cfg.source}</strong> · mode: <strong>{cfg.config.mode || 'graph'}</strong>
          </span>
        )}
      </div>

      {/* Mode selector — admin-only. Determines which credential set is used. */}
      <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 4 }}>
        <strong className="small">Transport mode:</strong>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <input type="radio" name="mode" value="graph" checked={isGraph} onChange={() => setForm({ ...form, mode: 'graph' })} />
          <span>Microsoft Graph (recommended — no user password)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <input type="radio" name="mode" value="smtp" checked={!isGraph} onChange={() => setForm({ ...form, mode: 'smtp' })} />
          <span>SMTP AUTH (legacy)</span>
        </label>
      </div>

      {/* Settings for the chosen mode: Graph (Microsoft 365 app) or SMTP server. */}
      {isGraph ? (
        <>
          <div className="note" style={{ marginBottom: '0.75rem' }}>
            Uses the Entra app registration's client credentials to call Microsoft Graph <code>/sendMail</code>.
            Requires: an app registration in Entra with the <strong>Mail.Send</strong> Application permission,
            an Exchange <strong>Application Access Policy</strong> restricting the app to just the sending
            mailbox, and admin consent granted.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '12em 1fr', gap: '0.5rem 0.75rem', alignItems: 'center' }}>
            <label>Tenant ID</label>
            <input value={form.tenantId} placeholder="00000000-0000-0000-0000-000000000000"
                   onChange={e => setForm({ ...form, tenantId: e.target.value.trim() })} />
            <label>Client ID</label>
            <input value={form.clientId} placeholder="00000000-0000-0000-0000-000000000000"
                   onChange={e => setForm({ ...form, clientId: e.target.value.trim() })} />
            <label>Client secret</label>
            <input type="password" value={form.clientSecret} placeholder="leave masked to keep existing"
                   onChange={e => setForm({ ...form, clientSecret: e.target.value })} />
            <label>Send from mailbox</label>
            <input value={form.from} placeholder="accounts@ecovac.ca"
                   onChange={e => setForm({ ...form, from: e.target.value.trim() })} />
            <label>Display name</label>
            <input value={form.fromName} placeholder="EcoVac Solutions Ltd."
                   onChange={e => setForm({ ...form, fromName: e.target.value })} />
          </div>
        </>
      ) : (
        <>
          <div className="note" style={{ marginBottom: '0.75rem' }}>
            SMTP AUTH via <code>nodemailer</code>. For Office 365 use host <code>smtp.office365.com</code>,
            port <code>587</code>, and an <strong>app password</strong> (create at Microsoft account → Security →
            Advanced security options if MFA is on). Microsoft is deprecating SMTP AUTH in 2025 — prefer Graph mode.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '12em 1fr', gap: '0.5rem 0.75rem', alignItems: 'center' }}>
            <label>Host</label>
            <input value={form.host} placeholder="smtp.office365.com"
                   onChange={e => setForm({ ...form, host: e.target.value.trim() })} />
            <label>Port</label>
            <input value={form.port} placeholder="587"
                   onChange={e => setForm({ ...form, port: e.target.value.trim() })} />
            <label>Username (mailbox)</label>
            <input value={form.user} placeholder="skumar@ecovac.ca"
                   onChange={e => setForm({ ...form, user: e.target.value.trim() })} />
            <label>Password / app password</label>
            <input type="password" value={form.pass} placeholder="leave masked to keep existing"
                   onChange={e => setForm({ ...form, pass: e.target.value })} />
            <label>Send from mailbox</label>
            <input value={form.from} placeholder="accounts@ecovac.ca (can differ from username)"
                   onChange={e => setForm({ ...form, from: e.target.value.trim() })} />
            <label>Display name</label>
            <input value={form.fromName} placeholder="EcoVac Solutions Ltd."
                   onChange={e => setForm({ ...form, fromName: e.target.value })} />
          </div>
        </>
      )}

      {/* Internal recipient — receives the SUMMARY PDF on bulk statement sends.
          Blank = disabled. */}
      <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 4 }}>
        <label className="small" style={{ display: 'block', marginBottom: '0.25rem' }}>
          <strong>Internal summary recipient (optional):</strong>
        </label>
        <input type="email" placeholder="pino@ecovac.ca — leave blank to disable"
               value={form.summaryRecipient}
               onChange={e => setForm({ ...form, summaryRecipient: e.target.value.trim() })}
               style={{ width: '100%', maxWidth: '32em' }} />
        <div className="small" style={{ color: '#666', marginTop: '0.25rem' }}>
          When statements are sent to customers in bulk, the SUMMARY PDF (all customers rolled up)
          is also emailed here for internal review.
        </div>
      </div>

      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="refresh-btn" onClick={save} disabled={busy}
                style={{ background: '#15803d' }}>
          {busy ? 'Saving…' : 'Save configuration'}
        </button>
      </div>

      {/* Send a test email. */}
      <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <strong className="small">Send a test email:</strong>
          <input type="email" placeholder="you@ecovac.ca" value={testTo}
                 onChange={e => setTestTo(e.target.value)}
                 style={{ padding: '3px 6px', minWidth: '18em' }} />
          <button className="refresh-btn" onClick={sendTest} disabled={busy || !testTo}
                  style={{ background: '#0369a1' }}>
            📧 Send test
          </button>
        </div>
        <div className="small" style={{ color: '#666', marginTop: '0.4rem' }}>
          Uses the currently-saved config in the mode selected above. If this test lands, statement emails will too.
        </div>
      </div>

      {msg && <div className="small" style={{ color: '#166534', marginTop: '0.5rem' }}>{msg}</div>}
      {err && <div className="small" style={{ color: '#991b1b', marginTop: '0.5rem' }}>{err}</div>}
    </div>
  );
}
